import { GenericObjectInterface, routes } from "@/src/utilities";
import { useMemo, useState, useEffect } from "react";

import { useAppSelector } from "@/src/store/hooks";
import { transformCategories } from "@/src/utilities/helper/helper";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

interface MegaMenuProps {
  mobile?: boolean;
  setIsMenuOpen?: (isOpen: boolean) => void;
}

function MegaMenu({ mobile = false, setIsMenuOpen }: MegaMenuProps) {
  const pathname = usePathname();
  const { productCategories } = useAppSelector(
    (root) => root.unGatedContentStateReducer
  );
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Mobile state
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  /**
   * navItems is an array of objects that contains the label, href, hasDropdown, and subitems
   * @param label - the label of the menu item
   * @param href - the href of the menu item
   * @param hasDropdown - the type of dropdown
   * link - for single link
   * nested - for nested dropdown
   * flat - for flat dropdown
   * @param subitems - the subitems of the menu item
   */
  const navItems: any[] = useMemo(() => {
    return [
      {
        label: "Technology Reviews",
        hasDropdown: "nested",
        subitems: transformCategories(productCategories),
      },
      { label: "Compare Tools", href: "/compare-tools", hasDropdown: "link" },
      {
        label: "Resource Center",
        hasDropdown: "flat",
        subitems: [
          {
            label: "Whitepapers",
            href: `/${routes.WHITEPAPERS}`,
          },
          {
            label: "Case Studies",
            href: `/${routes.CASE_STUDIES}`,
          },
          {
            label: "Webinars and Videos",
            href: `/${routes.WEBINARS}`,
          },
        ],
      },
      {
        label: "Thought Leadership",
        hasDropdown: "flat",
        subitems: [
          {
            label: "Blog",
            href: `/${routes.BLOG}`,
          },
          {
            label: "FAQ Section",
            href: `/${routes.FAQ}`,
          },
          {
            label: "Interviews and Podcasts",
            href: `/${routes.INTERVIEWS_PODCASTS}`,
          },
          {
            label: "Infographics",
            href: `/${routes.INFOGRAPHICS}`,
          },
        ],
      },
      { label: "Tech News", href: `/${routes.NEWS}`, hasDropdown: "link" },
      // {
      //   label: "Newsletters",
      //   href: `/${routes.NEWSLETTERS}`,
      //   hasDropdown: "link",
      // },
      {
        label: "Vendor Directory",
        href: `/${routes.VENDORS}`,
        hasDropdown: "link",
      },
      {
        label: "Advertise With Us",
        href: `/${routes.CONTACT}`,
        hasDropdown: "link",
      },
    ];
  }, [productCategories]);
  const [activeCategory, setActiveCategory] = useState<any>(null);

  // Update activeCategory when productCategories loads
  useEffect(() => {
    if (productCategories?.length > 0 && !activeCategory) {
      const transformedCategories = transformCategories(productCategories);
      if (transformedCategories?.length > 0) {
        setActiveCategory(transformedCategories[0]);
      }
    }
  }, [productCategories]);

  // Track scroll position for color transition
  const [hasScrolled, setHasScrolled] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    const updateScroll = (latest: number) => {
      // Check if scrolled past h-screen (viewport height)
      const heroHeight = window.innerHeight;
      setHasScrolled(latest > heroHeight);
    };

    const unsubscribe = scrollY.on("change", updateScroll);
    return () => unsubscribe();
  }, [scrollY]);

  const isActive = (item: any, pathname: string): boolean => {
    if (item.href && pathname === item.href) return true;

    // For nested/flat menus, check subitems recursively
    if (item.subitems) {
      return item.subitems.some((sub: any) => isActive(sub as any, pathname));
    }

    return false;
  };

  const toggleExpanded = (label: string) => {
    setExpandedItem(expandedItem === label ? null : label);
  };

  const toggleCategory = (label: string) => {
    setExpandedCategory(expandedCategory === label ? null : label);
  };

  if (mobile) {
    return (
      <div className="flex flex-col w-full px-4 space-y-2">
        {navItems.map((item) => (
          <div
            key={item.label}
            className="border-b border-border/50 last:border-0"
          >
            {item.subitems ? (
              <div>
                <button
                  onClick={() => toggleExpanded(item.label)}
                  className="flex items-center justify-between w-full py-3 text-sm font-medium text-foreground"
                >
                  {item.label}
                  {expandedItem === item.label ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </button>
                <AnimatePresence>
                  {expandedItem === item.label && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 pb-2 space-y-2">
                        {item.hasDropdown === "nested"
                          ? // Nested Categories (Technology Reviews)
                          item.subitems.map((cat: any) => (
                            <div key={cat.name}>
                              <button
                                onClick={() => toggleCategory(cat.name)}
                                className="flex items-center justify-between w-full py-2 text-sm text-muted-foreground hover:text-foreground"
                              >
                                <span className="flex items-center gap-2">
                                  {cat.icon && (
                                    <img
                                      src={cat.icon}
                                      className="h-4 w-4"
                                      alt=""
                                    />
                                  )}
                                  {cat.name}
                                </span>
                                {expandedCategory === cat.name ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : (
                                  <ChevronDown className="h-3 w-3" />
                                )}
                              </button>
                              <AnimatePresence>
                                {expandedCategory === cat.name && (
                                  <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: "auto", opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    className="overflow-hidden pl-4 border-l border-border/50 ml-1"
                                  >
                                    <div className="flex flex-col space-y-2 py-2">
                                      {cat.subcategories?.map(
                                        (link: GenericObjectInterface) => (
                                          <Link
                                            key={link.href}
                                            href={`/${routes.PRODUCTS}/category/${cat.documentId}?subCategory=${link.documentId}`}
                                            onClick={() => {
                                              if (setIsMenuOpen)
                                                setIsMenuOpen(false);
                                            }}
                                            className="text-xs text-muted-foreground hover:text-primary text-left py-1"
                                          >
                                            {link.name}
                                          </Link>
                                        )
                                      )}
                                      <Link
                                        href={`/${routes.PRODUCTS}?category=${cat.documentId}&subCategory=${cat.subcategories?.[0]?.documentId}`}
                                        className="text-xs font-medium text-primary py-1"
                                        onClick={() => {
                                          if (setIsMenuOpen)
                                            setIsMenuOpen(false);
                                        }}
                                      >
                                        View all
                                      </Link>
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          ))
                          : // Flat List (Resource Center, Thought Leadership)
                          item.subitems.map((subitem: any) => (
                            <Link
                              key={subitem.label}
                              href={subitem.href}
                              className="block py-2 text-sm text-muted-foreground hover:text-foreground"
                              onClick={() => {
                                if (setIsMenuOpen) setIsMenuOpen(false);
                              }}
                            >
                              {subitem.label}
                            </Link>
                          ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                href={item.href!}
                className="block py-3 text-sm font-medium text-foreground"
                onClick={() => {
                  if (setIsMenuOpen) setIsMenuOpen(false);
                }}
              >
                {item.label}
              </Link>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-2 xl:gap-6 justify-between">
      <NavigationMenu viewport={false}>
        <NavigationMenuList className="flex-wrap">
          {navItems?.map((item) => {
            const active = isActive(item, pathname);
            // console.log(active, item, "active");

            if (item.subitems) {
              // console.log(item, "item");
              if (item.hasDropdown === "nested") {
                return (
                  <NavigationMenuItem key={item.label}>
                    <NavigationMenuTrigger
                      className={`py-3 text-sm font-medium ${pathname === "/"
                        ? hasScrolled
                          ? active
                            ? "text-[hsl(var(--primary))] "
                            : "text-[rgb(0, 0, 0)] "
                          : "text-[rgb(0, 0, 0)] "
                        : active
                          ? "text-[hsl(var(--primary))] "
                          : "text-[rgb(0, 0, 0)] "
                        }`}
                    >
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid grid-cols-[220px_1fr] w-[700px] ">
                        {/* Left categories */}
                        <div className="flex flex-col border-r cursor-pointer">
                          {item.subitems.map((cat: any) => (
                            <button
                              key={cat.name}
                              onMouseEnter={() => setActiveCategory(cat as any)}
                              className={`flex text-sm font-medium items-center text-left gap-2 px-4 py-3 cursor-pointer
                          ${activeCategory?.name === cat.name
                                  ? "bg-aims-gradient"
                                  : ""
                                }`}
                            >
                              <img src={cat.icon} className="h-6 w-6" />
                              <div className="text-sm flex items-center gap-2">
                                <p className="w-[120px] text-left font-medium text-black text-sm">
                                  {cat.name}
                                </p>{" "}
                                <ChevronRight className="h-5 w-5 ml-2" />
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Right panel with sub-subitems */}
                        <div className="grid grid-cols-2 gap-4 px-6 py-2">
                          {activeCategory?.subcategories?.map(
                            (link: GenericObjectInterface) => (
                              <Link
                                key={link.name}
                                href={link.href}
                                onClick={() => setOpenDropdown(null)}
                                className="text-xs capitalize font-semibold text-muted-foreground hover:text-primary text-left cursor-pointer"
                              >
                                {link.name}
                              </Link>
                            )
                          )}

                          {/* View all at the bottom right*/}
                          <div className="col-span-2 flex justify-end mt-4">
                            <Link
                              href={`/${routes.PRODUCTS}?category=${activeCategory?.documentId}&subCategory=${activeCategory?.subcategories?.[0]?.documentId}`}
                              className="text-sm font-medium text-primary"
                              onClick={() => setOpenDropdown(null)}
                            >
                              View all
                            </Link>
                          </div>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              } else if (item.hasDropdown === "flat") {
                return (
                  <NavigationMenuItem key={item.label}>
                    <NavigationMenuTrigger
                      className={`py-3 text-sm font-medium ${pathname === "/"
                        ? hasScrolled
                          ? active
                            ? "text-[hsl(var(--primary))] "
                            : "text-[rgb(0, 0, 0)] "
                          : "text-[rgb(0, 0, 0)] "
                        : active
                          ? "text-[hsl(var(--primary))] "
                          : "text-[rgb(0, 0, 0)] "
                        }`}
                    >
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="flex flex-col w-[200px]">
                        {item.subitems?.map((subitem: any) => (
                          <NavigationMenuLink
                            key={subitem?.label}
                            asChild
                            onClick={() => setOpenDropdown(null)}
                          // className={navigationMenuTriggerStyle()}
                          >
                            <Link href={subitem?.href}>{subitem?.label}</Link>
                          </NavigationMenuLink>
                        ))}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              } else if (item.hasDropdown === "link") {
                return (
                  <NavigationMenuItem key={item.label}>
                    <NavigationMenuTrigger
                      className={`py-3 text-sm font-medium ${pathname === "/"
                        ? hasScrolled
                          ? active
                            ? "text-[hsl(var(--primary))] "
                            : "text-[rgb(0, 0, 0)] "
                          : "text-[rgb(0, 0, 0)] "
                        : active
                          ? "text-[hsl(var(--primary))] "
                          : "text-[rgb(0, 0, 0)] "
                        }`}
                    >
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <NavigationMenuLink
                        asChild
                        onClick={() => setOpenDropdown(null)}
                      >
                        <Link
                          href={item.href!}
                          onClick={() => setOpenDropdown(null)}
                        >
                          {item.label}
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              }
            } else {
              return (
                <NavigationMenuItem key={item?.label}>
                  <NavigationMenuLink
                    asChild
                    onClick={() => setOpenDropdown(null)}
                  >
                    <Link
                      href={item?.href}
                      className={`py-3 text-sm font-medium ${pathname === "/"
                        ? hasScrolled
                          ? active
                            ? "text-[hsl(var(--primary))] "
                            : "text-[rgb(0, 0, 0)] "
                          : "text-[rgb(0, 0, 0)] "
                        : active
                          ? "text-[hsl(var(--primary))] "
                          : "text-[rgb(0, 0, 0)] "
                        }`}
                    >
                      {item?.label}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              );
            }
          })}
        </NavigationMenuList>
      </NavigationMenu>
    </div>
  );
}

export default MegaMenu;
